import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { DATA_HOOKS } from './constants';

import {
  calendarUniDriverFactory,
  CalendarUniDriver,
} from './Calendar/Calendar.uni.driver';

export interface DatePickerDriver extends BaseUniDriver {
  getCurrentMonthWithYear(): Promise<string>;
  getNthWeekDayName(n: number): Promise<string>;
  clickOnNthDay(n: number): Promise<any>;
  clickDay(date: Date): Promise<any>;
  isDayActive(date: Date): Promise<boolean>;
  clickOnYearDropdown(): Promise<any>;
  clickOnMonthDropdown(): Promise<any>;
  clickOnNthYear(n: number): Promise<any>;
  clickOnPrevMonthButton(): Promise<any>;
  clickOnNextMonthButton(): Promise<any>;
  isYearDropdownExists(): Promise<boolean>;
  isYearCaptionExists(): Promise<boolean>;
  isMonthDropdownExists(): Promise<boolean>;
  isMonthCaptionExists(): Promise<boolean>;
  getMonthCaption(): Promise<string>;
  getMonthDropdownLabel(): Promise<string>;
  getSelectedYear(): Promise<string>;
  getFocusedDay(): Promise<string | null>;
  pressLeftArrowKey(): Promise<any>;
  pressRightArrowKey(): Promise<any>;
  getSelectedDay(): Promise<string>;
  getMonthDropdownDriver: CalendarUniDriver['getMonthDropdownDriver'];
  getYearDropdownDriver: CalendarUniDriver['getYearDropdownDriver'];
}

export const datePickerDriverFactory = (base: UniDriver): DatePickerDriver => {
  const calendarNode = base.$(`[data-hook="${DATA_HOOKS.CALENDAR}"]`);
  const calendarDriver = calendarUniDriverFactory(calendarNode);

  return {
    ...baseUniDriverFactory(base),

    /**
     * Get the month and years labels of the current displayed month in the following format `{Month} {year}`
     */
    getCurrentMonthWithYear: async () =>
      calendarDriver.getCurrentMonthWithYear(),
    /**
     * Get the the day name label of a specific date
     * @param n - the day index
     */
    getNthWeekDayName: async (n: number) => calendarDriver.getNthWeekDayName(n),
    /**
     * Click on the Nth day in the current displayed month
     * @param n - the day index
     */
    clickOnNthDay: async (n: number) => calendarDriver.clickOnNthDay(n),
    /**
     * Click on a given day
     * @param date - a date to select
     */
    clickDay: async (date: Date) => calendarDriver.clickDay(date),
    /**
     * Check if the given day is active
     * @param date - a date
     */
    isDayActive: async (date: Date) => calendarDriver.isDayActive(date),
    /**
     * Click on the year dropdown
     */
    clickOnYearDropdown: async () => calendarDriver.clickOnYearDropdown(),
    /**
     * Click on the month dropdown
     */
    clickOnMonthDropdown: async () => calendarDriver.clickOnMonthDropdown(),
    /**
     * Click on the Nth dropdown option in the year dropdown
     * @param n - the index of the year in the list
     */
    clickOnNthYear: async (n: number) => calendarDriver.clickOnNthYear(n),
    /**
     * Click on the prev month button
     */
    clickOnPrevMonthButton: async () => calendarDriver.clickOnPrevMonthButton(),
    /**
     * Click on the next month button
     */
    clickOnNextMonthButton: async () => calendarDriver.clickOnNextMonthButton(),
    /**
     * Check whether the year dropdown exist
     */
    isYearDropdownExists: async () => calendarDriver.isYearDropdownExists(),
    /**
     * Check whether the year caption exist
     */
    isYearCaptionExists: async () => calendarDriver.isYearCaptionExists(),
    /**
     * Check whether the month dropdown exist
     */
    isMonthDropdownExists: async () => calendarDriver.isMonthDropdownExists(),
    /**
     * Check whether the month caption exist
     */
    isMonthCaptionExists: async () => calendarDriver.isMonthCaptionExists(),
    /**
     * Get the month caption text
     */
    getMonthCaption: async () => calendarDriver.getMonthCaption(),
    /**
     * Get the label of the selected month dropdown option
     */
    getMonthDropdownLabel: async () => calendarDriver.getMonthDropdownLabel(),
    /**
     * Get the label of the selected year dropdown option
     */
    getSelectedYear: async () => calendarDriver.getSelectedYear(),
    /**
     *  Returns the text of the focused day or `null` if there is no focused day
     */
    getFocusedDay: async () => calendarDriver.getFocusedDay(),
    /**
     * Simulating pressing on the left arrow key in the keyboard
     */
    pressLeftArrowKey: async () => calendarDriver.pressLeftArrow(),
    /**
     * Simulating pressing on the right arrow key in the keyboard
     */
    pressRightArrowKey: async () => calendarDriver.pressRightArrow(),
    /**
     * Get the selected day
     */
    getSelectedDay: async () => calendarDriver.getSelectedDay(),
    /**
     * Get the month dropdown driver
     */
    getMonthDropdownDriver: calendarDriver.getMonthDropdownDriver,
    /**
     * Get the year dropdown driver
     */
    getYearDropdownDriver: calendarDriver.getYearDropdownDriver,
  };
};
